// The map (docs/ARCHITECTURE.md §2.5): the one component that owns MapLibre. React never reaches into the map and
// map code never reaches into React: everything map-side lives in MapController, which talks to the rest through the
// stores. The shared decluttered style (shared/map/style.json) is bundled as a fingerprinted file.
import * as maplibregl from "maplibre-gl";
import { useEffect, useRef, useState } from "react";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import styleUrl from "../../../../shared/map/style.json?url";
import { emojiOfCode } from "./draw";
import { MapController } from "./MapController";
import { reducedMotion } from "./slide";
import "./map-dom.css";
import styles from "./EventMap.module.css";

// MapLibre draws tiles in a background worker; the build emits it as its own fingerprinted file.
maplibregl.setWorkerUrl(workerUrl);

type MapState = "loading" | "loaded" | "error";

declare global {
  interface Window { nowishDebug?: { map?: maplibregl.Map; controller?: MapController; emojiOfCode?: (code: number) => string } }
}

export function EventMap({ center, zoom }: { center: { lat: number; lng: number }; zoom: number }) {
  const container = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<MapState>("loading");

  useEffect(() => {
    const el = container.current;
    if (!el) return;
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: el, style: styleUrl, center: [center.lng, center.lat], zoom,
        attributionControl: { compact: true },
        // Labels fade in and out as usual. The grouped pins' crossfade on a zoom step happens under the zoom slide
        // (slide.ts), which hides them while it draws them sliding. Reduced motion: everything swaps in one frame.
        fadeDuration: reducedMotion() ? 0 : 300,
      });
    } catch (err) {
      // No WebGL, or the browser refused a context: a notice over the empty map, not a broken page (§2.6).
      console.warn("Nowish: the map can't start", err);
      setState("error");
      return;
    }
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    let loaded = false;
    map.once("load", () => { loaded = true; setState("loaded"); });
    // Only a style that can't load is fatal (a notice over the map); a tile that fails just leaves a gap.
    map.on("error", (e: maplibregl.ErrorEvent & { sourceId?: string }) => {
      console.warn("Nowish: map error", e.error);
      if (!loaded && !e.sourceId) setState("error");
    });
    const controller = new MapController(map);
    // For tests and the console: the map, its controller, and which emoji a pin image's code stands for.
    window.nowishDebug = { ...window.nowishDebug, map, controller, emojiOfCode };
    return () => { controller.destroy(); map.remove(); };
    // The map is created once; moving it later goes through the map, not new props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.map} aria-label="Map" data-map-state={state}>
      <div ref={container} className={styles.canvas} />
      {state === "error" && <div className={styles.notice} role="status">The map can't load in this browser.</div>}
    </div>
  );
}
